
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CandidateComment } from "@/types/candidate";
import { AddCommentModal } from "./AddCommentModal";
import { CommentModal } from "./CommentModal";
import { formatDistanceToNow } from "date-fns";
import { triggerReanalysis } from "@/utils/candidate/ai";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";

interface CandidateCommentsProps {
  comments: CandidateComment[];
  candidateId: string;
  cv?: string | null;
  handleAddComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
}

export const CandidateComments = ({
  comments,
  candidateId,
  cv,
  handleAddComment,
  handleUpdateComment,
  handleDeleteComment
}: CandidateCommentsProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState<CandidateComment | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Calculate pagination
  const totalPages = Math.ceil(comments.length / itemsPerPage);
  const indexOfLastComment = currentPage * itemsPerPage;
  const indexOfFirstComment = indexOfLastComment - itemsPerPage;
  const currentComments = comments
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(indexOfFirstComment, indexOfLastComment);

  const onAddComment = async (content: string) => {
    try {
      await handleAddComment(content);
      setIsAddModalOpen(false);
      
      if (cv) {
        setIsAnalyzing(true);
        await triggerReanalysis(candidateId, cv, content);
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setIsAnalyzing(false);
    }
  };

  const onUpdateComment = async (content: string) => {
    if (!currentComment) return;
    
    try {
      await handleUpdateComment(currentComment.id, content);
      setIsEditModalOpen(false);
      setCurrentComment(null);
      
      if (cv) {
        setIsAnalyzing(true);
        await triggerReanalysis(candidateId, cv, content);
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      setIsAnalyzing(false);
    }
  };

  const onDeleteComment = async () => {
    if (!currentComment) return;
    
    try {
      await handleDeleteComment(currentComment.id);
      setIsEditModalOpen(false);
      setCurrentComment(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };
  
  // Handle pagination
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4 pr-10">
        <h3 className="text-lg font-semibold">Recruiter Comments</h3>
        <Button 
          onClick={() => setIsAddModalOpen(true)}
          disabled={isAnalyzing}
          className="bg-digger-blue hover:bg-digger-blue-light text-white flex items-center gap-2 px-4 py-2"
        >
          <Plus className="h-5 w-5" />
          {isAnalyzing ? 'Analyzing...' : 'Add Comment'}
        </Button>
      </div>
      
      {comments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No comments yet. Add one to track your notes about this candidate.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {currentComments.map(comment => (
            <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-sm font-medium">{comment.created_by}</p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setCurrentComment(comment);
                    setIsEditModalOpen(true);
                  }}
                >
                  Edit
                </Button>
              </div>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: comment.content }} />
            </div>
          ))}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={page === currentPage}
                      onClick={() => handlePageChange(page)}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
      
      <AddCommentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={onAddComment}
      />
      
      {currentComment && (
        <CommentModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setCurrentComment(null);
          }}
          onSave={onUpdateComment}
          onDelete={onDeleteComment}
          initialContent={currentComment.content}
        />
      )}
    </div>
  );
};
